import React, {useEffect, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';
import {debounce} from 'debounce';
import {setkpiLandingPageOn} from '../features/Statsview/dashboardSlice';
import {fetchPostAuthSafe} from '../app/utils';
import {useTranslation} from 'react-i18next';
import {SidebarNavTooltip} from './GeneralComponents';

// Grower Extensions: normalize a registry icon value into a full Font Awesome class.
// Accepts a full class ('fas fa-fw fa-grip'), a short name ('fa-grip'), a bare name
// ('grip'), or empty. Defaults to the 6-dot grip icon when nothing is provided.
function normalizeExtensionIcon(icon) {
  const DEFAULT_ICON = 'fas fa-fw fa-grip';
  const raw = (icon || '').trim();
  if (raw === '') {
    return DEFAULT_ICON;
  }
  // Already a full class (has a style token like fas/far/fab or the fixed-width flag)
  if (/\b(fa[srlbdt]|fa-fw)\b/.test(raw)) {
    return raw;
  }
  const name = raw.startsWith('fa-') ? raw : `fa-${raw}`;
  return `fas fa-fw ${name}`;
}

function Sidebar(props) {
  // Declare/import additional hooks
  const dispatch = useDispatch();
  const {t} = useTranslation();
  // Declare/import external states from store
  const displaySettings = useSelector((state) => {
    return state.framework;
  });
  const customerSettings = useSelector((state) => {
    return state.app.customerSettings;
  });
  const authenticationStatus = useSelector((state) => {
    return state.app.authenticationStatus;
  });
  const userSettings = useSelector((state) => {
    return state.app.userSettings;
  });
  // Component effects
  // Set listener for ui clicks outside of the side
  // Held in a ref so the document listener below can stay mounted once for the component's
  // lifetime without closing over a stale `props.handleSetSideMenuOpened`.
  const closeSidebarRef = useRef(null);
  closeSidebarRef.current = () => {
    return closeSidebar();
  };

  useEffect(() => {
    // Close sidebar if clicked outside of sidebar.
    // This was a jQuery `$(document).on('click', ...)` with no teardown, so every remount of
    // the sidebar added another handler that was never removed.
    function handleDocumentClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const sidebar = document.getElementById('accordionSidebar');
      if (
        !target.closest('#accordionSidebar') &&
        !target.closest('#sidebarToggleTop') &&
        !(sidebar && sidebar.classList.contains('toggled'))
      ) {
        closeSidebarRef.current();
      }
    }

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  // Component functions
  // Sidebar set open
  function openSidebar() {
    props.handleSetSideMenuOpened(true);
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const menu = urlParams.get('menu');
    if (menu == 'open') {
      openSidebar();
    }
  }, [window]);

  // Sidebar set close
  function closeSidebar() {
    props.handleSetSideMenuOpened(false);
  }

  // Nav to landing
  function closeSidebarNavToDashboard() {
    props.handleSetSideMenuOpened(false);
    dispatch(setkpiLandingPageOn(true));
  }

  function navToDashboard() {
    dispatch(setkpiLandingPageOn(true));
  }

  const tooltipContentJustify = displaySettings.smallScreen || props.sideMenuOpened ? 'flex-start' : 'center';
  const tooltipPlacement = displaySettings.smallScreen ? 'bottom' : 'right';
  const disableSidebarTooltips = displaySettings.smallScreen;

  const inspectionNavLabel =
    authenticationStatus.inspectionEnabled && authenticationStatus.breakdownEnabled
      ? 'Inspection/Issue Report'
      : authenticationStatus.inspectionEnabled
      ? t('sidebarNav.inspection')
      : 'Issue Report';
  const inspectionNavIcon =
    !authenticationStatus.inspectionEnabled && authenticationStatus.breakdownEnabled
      ? 'fa-triangle-exclamation'
      : 'fa-list-check';

  return (
    <div id='sidenav'>
      <ul
        className={`navbar-nav bg-dark sidebar sidebar-dark accordion ${!props.sideMenuOpened && 'toggled'}`}
        id='accordionSidebar'
        style={{width: '100vw'}}
      >
        {/* Sidebar - Brand */}
        <div className='sidebar-brand px-2'>
          {displaySettings.smallScreen ? (
            <li className='nav-item'>
              <div>
                {props.sideMenuOpened && (
                  <div className='row w-100'>
                    <div className='w-100'>
                      <SidebarNavTooltip
                        disableTooltip={disableSidebarTooltips}
                        title={t('sidebarNav.home')}
                        contentJustify={tooltipContentJustify}
                        placement={tooltipPlacement}
                      >
                        <Link
                          className='nav-link w-100'
                          to='/'
                          aria-label={t('sidebarNav.home')}
                          onClick={debounce(closeSidebar, 150)}
                        >
                          <img src='/img/Logo_Text_NoSlogan_White.png' className='sidebar-logo' />
                        </Link>
                      </SidebarNavTooltip>
                    </div>
                    <div className='w-10'>
                      <button className='close sidenav-close' onClick={debounce(closeSidebar, 150)}>
                        <span>&times;</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </li>
          ) : (
            <li className='nav-item'>
              <div>
                <SidebarNavTooltip
                  disableTooltip={disableSidebarTooltips}
                  title={t('sidebarNav.home')}
                  contentJustify={tooltipContentJustify}
                  placement={tooltipPlacement}
                >
                  <Link
                    className='nav-link w-100 d-flex pl-0 py-2'
                    to='/'
                    aria-label={t('sidebarNav.home')}
                    onClick={props.sideMenuOpened ? closeSidebar : undefined}
                  >
                    <img
                      src={props.sideMenuOpened ? '/img/Logo_Text_NoSlogan_White.png' : '/img/Logo_Shared_White.png'}
                      className='sidebar-logo d-flex'
                    />
                  </Link>
                </SidebarNavTooltip>
              </div>
            </li>
          )}
        </div>

        {/* Divider */}
        <hr className='sidebar-divider d-flex align-items-center mt-3' />
        {window.hasOwnProperty('ReactNativeWebView') && (
          <li className={`nav-item ${props.activePage == 'scanning' && 'active'}`}>
            <Link
              className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
              to='#'
              aria-label={t('sidebarNav.scan')}
              onClick={(e) => {
                e.preventDefault();
                // Switch to native Scan immediately — do not await telemetry POST
                // (awaiting it delayed postMessage until getCustomerInfo/etc finished).
                try {
                  window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                      url: '/',
                      data: JSON.stringify({
                        redirect: 'scan',
                        staleVersion: userSettings.staleVersion,
                        username: userSettings.username,
                        customerName: customerSettings.name,
                      }),
                    })
                  );
                } catch (err) {
                  console.error('Failed to post scan redirect to native shell', err);
                }
                void fetchPostAuthSafe(
                  '/scanLinkClicked',
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'sessionsyncusername': userSettings.username,
                      'sessionsyncdb': customerSettings.name,
                    },
                    body: JSON.stringify({'value': true}),
                  },
                  userSettings.username,
                  customerSettings.name
                );
              }}
            >
              <i className={`fa-solid fa-qrcode`} />
              <span className={`nav-name`}>&nbsp;Scan</span>
            </Link>
          </li>
        )}

        {/* Nav Item - CabView */}
        {authenticationStatus.cabviewEnabled && (
          <li className={`nav-item ${props.activePage == 'cabview' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.cabview')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/cabview'
                aria-label={t('sidebarNav.cabview')}
                onClick={displaySettings.smallScreen ? closeSidebar : undefined}
              >
                <i className='fas fa-fw fa-tractor' />
                <span className='nav-name'>&nbsp;CabView</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}

        {/* Nav Item - CropView */}
        {authenticationStatus.cropviewEnabled && (
          <li className={`nav-item ${props.activePage == 'cropview' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.cropview')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/cropview'
                aria-label={t('sidebarNav.cropview')}
                onClick={displaySettings.smallScreen ? closeSidebar : undefined}
              >
                <i className='fas fa-fw fa-seedling' />
                <span className='nav-name'>&nbsp;CropView</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}

        {/* Nav Item - ShopView */}
        {authenticationStatus.shopviewEnabled && (
          <li className={`nav-item ${props.activePage == 'shopview' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.shopview')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/shopview'
                aria-label={t('sidebarNav.shopview')}
                onClick={displaySettings.smallScreen ? closeSidebar : undefined}
              >
                <i className='fas fa-fw fa-wrench' />
                <span className='nav-name'>&nbsp;ShopView</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}

        {/* Nav Item - Inspection / Issue Report */}
        {(authenticationStatus.inspectionEnabled || authenticationStatus.breakdownEnabled) &&
          (displaySettings.smallScreen || customerSettings.inspection.inspectionFormWideModeEnabled) && (
            <li className={`nav-item ${props.activePage == 'inspectionForm' && 'active'}`}>
              <SidebarNavTooltip
                disableTooltip={disableSidebarTooltips}
                title={inspectionNavLabel}
                contentJustify={tooltipContentJustify}
                placement={tooltipPlacement}
              >
                <Link
                  className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                  to='/inspectionForm'
                  aria-label={inspectionNavLabel}
                  onClick={displaySettings.smallScreen ? closeSidebar : undefined}
                >
                  <i className={`fas fa-fw ${inspectionNavIcon}`} />
                  <span className='nav-name'>&nbsp;{inspectionNavLabel}</span>
                </Link>
              </SidebarNavTooltip>
            </li>
          )}

        {/* Nav Item - Labor (Clock In/Out) */}
        {authenticationStatus.laborEnabled && (
          <li className={`nav-item ${props.activePage == 'labor' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.labor')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/labor'
                aria-label={t('sidebarNav.labor')}
                onClick={displaySettings.smallScreen ? closeSidebar : undefined}
              >
                <i className='fas fa-fw fa-user-clock' />
                <span className='nav-name'>&nbsp;{t('sidebarNav.labor')}</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}

        {/* Nav Item - AlertView */}
        {authenticationStatus.alertviewEnabled && (
          <li className={`nav-item ${props.activePage == 'alertview' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.alertview')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/alertview'
                aria-label={t('sidebarNav.alertview')}
                onClick={displaySettings.smallScreen ? closeSidebar : undefined}
              >
                <i className='fas fa-fw fa-bell' />
                <span className='nav-name'>&nbsp;AlertView</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}

        {/* Nav Item - StatsView / Dashboard */}
        {authenticationStatus.dashboardEnabled && (
          <li className={`nav-item ${props.activePage == 'statsview' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.statsview')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/statsview'
                aria-label={t('sidebarNav.statsview')}
                onClick={displaySettings.smallScreen ? closeSidebarNavToDashboard : navToDashboard}
              >
                <i className='fa fa-bar-chart' />
                <span className='nav-name'>&nbsp;StatsView</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}

        {/* Nav Item - MapView / ZoneInfo */}
        {/* Can display zoneinfo for samsara only accounts if geofences are availible */}
        {customerSettings.general.geoFencesProvided && authenticationStatus.zoneInfoEnabled && (
          <li className={`nav-item ${props.activePage == 'mapview' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.mapview')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/mapview'
                aria-label={t('sidebarNav.mapview')}
                onClick={displaySettings.smallScreen ? closeSidebar : undefined}
              >
                <i className='fas fa-fw fa-map-location-dot' />
                <span className='nav-name'>&nbsp;MapView</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}
        {/* Nav Item - Insights */}
        {authenticationStatus.dashboardEnabled && authenticationStatus.aiChatEnabled && (
          <li className={`nav-item ${props.activePage == 'insights' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.insights')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/insights'
                aria-label={t('sidebarNav.insights')}
                onClick={displaySettings.smallScreen ? closeSidebar : undefined}
              >
                <i className='fas fa-fw fa-lightbulb' />
                <span className='nav-name'>&nbsp;Insights</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}

        {/* Nav Items - Grower Extensions (one per registry entry) */}
        {authenticationStatus.extensionsEnabled &&
          (authenticationStatus.extensions || []).map((extension) => {
            const iconClass = normalizeExtensionIcon(extension.icon);
            const isActive =
              props.activePage == 'extensions' && window.location.pathname == `/extensions/${extension.slug}`;
            return (
              <li key={extension.slug} className={`nav-item ${isActive && 'active'}`}>
                <SidebarNavTooltip
                  disableTooltip={disableSidebarTooltips}
                  title={extension.name}
                  contentJustify={tooltipContentJustify}
                  placement={tooltipPlacement}
                >
                  <Link
                    className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                    to={`/extensions/${extension.slug}`}
                    aria-label={extension.name}
                    onClick={() => {
                      // Signal the Extensions page to reset a drifted iframe back to the gallery.
                      // Navigation inside the embedded launcher doesn't change the portal URL, so
                      // clicking the already-active item is a no-op route change on its own.
                      window.dispatchEvent(new CustomEvent('ic-extensions-home', {detail: {slug: extension.slug}}));
                      if (displaySettings.smallScreen) {
                        closeSidebar();
                      }
                    }}
                  >
                    <i className={iconClass} />
                    <span className='nav-name'>&nbsp;{extension.name}</span>
                  </Link>
                </SidebarNavTooltip>
              </li>
            );
          })}

        {/* Nav Item - Audit Logs (ic_admin only) */}
        {userSettings.role === 'ic_admin' && (
          <li className={`nav-item ${props.activePage == 'auditlogs' && 'active'}`}>
            <SidebarNavTooltip
              disableTooltip={disableSidebarTooltips}
              title={t('sidebarNav.auditLogs')}
              contentJustify={tooltipContentJustify}
              placement={tooltipPlacement}
            >
              <Link
                className={`nav-link ${displaySettings.smallScreen && 'w-100'}`}
                to='/audit_logs'
                aria-label={t('sidebarNav.auditLogs')}
                onClick={displaySettings.smallScreen ? closeSidebar : undefined}
              >
                <i className='fas fa-fw fa-clipboard-list' />
                <span className='nav-name'>&nbsp;{t('sidebarNav.auditLogs')}</span>
              </Link>
            </SidebarNavTooltip>
          </li>
        )}
      </ul>
    </div>
  );
}

export {Sidebar};
